import { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  FormHelperText,
  CircularProgress,
  Typography,
  Paper,
} from '@material-ui/core';
import { TreeView, TreeItem } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import FolderIcon from '@material-ui/icons/Folder';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
  },
  paper: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    maxHeight: 500,
    overflow: 'auto',
  },
  treeView: {
    flexGrow: 1,
  },
  selectedPath: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.success.light,
    borderRadius: 4,
  },
  treeItem: {
    '&.Mui-selected > .MuiTreeItem-content': {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.common.white,
    },
  },
}));

interface GitLabTreeItem {
  id: string;
  name: string;
  path: string;
  type: 'tree' | 'blob';
}

interface GitLabTreePathPickerProps {
  onChange: (value: { path: string; repoPath: string }) => void;
  rawErrors?: string[];
  required?: boolean;
  schema: {
    title?: string;
    description?: string;
  };
  formData?: any;
  uiSchema?: {
    'ui:options'?: {
      gitlabProjectPath?: string;
      branch?: string;
      manifestRepoField?: string;  // 指向包含仓库信息的字段名
    };
  };
  formContext?: any;  // 表单上下文，可以访问其他字段的值
}

export const GitLabTreePathPicker = ({
  onChange,
  rawErrors,
  required,
  schema,
  formData,
  uiSchema,
  formContext,
}: GitLabTreePathPickerProps) => {
  const classes = useStyles();
  const [selectedPath, setSelectedPath] = useState(formData?.path || '');
  const [expanded, setExpanded] = useState<string[]>([]);
  const [treeData, setTreeData] = useState<Record<string, GitLabTreeItem[]>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 初始化时，如果 formData 存在但没有 path，清空它以确保 RJSF 验证正常工作
  // RJSF 对于 object 类型的必填字段，空对象 {} 不会触发验证，但 undefined 会
  useEffect(() => {
    if (formData && (!formData.path || !formData.repoPath)) {
      // 如果 formData 是空对象或没有 path/repoPath，清空它
      // 这样 RJSF 的 required 验证才能正常工作
      onChange(undefined as any);
      setSelectedPath('');
    }
  }, []); // 只在组件挂载时执行一次

  // 使用 Backstage 的 fetchApi (自动处理身份验证)
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  // 从表单上下文或父级数据中获取仓库信息
  const manifestRepoFieldName = uiSchema?.['ui:options']?.manifestRepoField || 'manifestRepo';
  const manifestRepoData = formContext?.formData?.[manifestRepoFieldName];

  const projectPath = manifestRepoData?.projectPath || uiSchema?.['ui:options']?.gitlabProjectPath || '';
  const branch = manifestRepoData?.defaultBranch || uiSchema?.['ui:options']?.branch || 'main';

  // 加载指定路径的子目录
  const loadTreeItems = async (path: string, repoPath: string) => {
    if (!repoPath) {
      return; // 没有仓库路径，不加载
    }

    const cacheKey = `${repoPath}:${path}`;
    if (treeData[cacheKey]) {
      return; // 已加载过
    }

    setLoading(true);
    setError('');
    try {
      const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
      const [owner, repo] = repoPath.split('/');
      if (!owner || !repo) {
        throw new Error(`Invalid project path: ${repoPath}`);
      }
      const url = `${baseUrl}/gitlab-helper/projects/${owner}/${repo}/tree?${path ? `path=${encodeURIComponent(path)}&` : ''}ref=${branch}`;

      const response = await fetchApi.fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch directory: ${response.statusText}`);
      }

      const data: GitLabTreeItem[] = await response.json();
      const folders = data.filter(item => item.type === 'tree');

      setTreeData(prev => ({
        ...prev,
        [cacheKey]: folders,
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载目录失败');
    } finally {
      setLoading(false);
    }
  };

  // 当 projectPath 变化时，重新加载根目录
  useEffect(() => {
    if (projectPath) {
      // 清空之前的数据
      setTreeData({});
      setExpanded([]);
      setSelectedPath('');
      // 清空 formData，确保验证正常工作
      onChange(undefined as any);
      // 重新加载
      loadTreeItems('', projectPath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectPath, branch]);

  // 处理节点展开/收起
  const handleToggle = (_event: any, nodeIds: string[]) => {
    setExpanded(nodeIds);

    // 加载新展开的节点
    if (projectPath) {
      nodeIds.forEach(nodeId => {
        const cacheKey = `${projectPath}:${nodeId}`;
        if (!treeData[cacheKey]) {
          loadTreeItems(nodeId, projectPath);
        }
      });
    }
  };

  // 处理路径选择（通过 Checkbox）
  const handlePathSelect = (path: string) => {
    setSelectedPath(path);
    // 确保传递完整的对象结构，包含 path 和 repoPath
    onChange({
      path: path,
      repoPath: projectPath,
    });
  };

  // 递归渲染树节点
  const renderTree = (items: GitLabTreeItem[], repoPath: string) => {
    return items.map(item => {
      const cacheKey = `${repoPath}:${item.path}`;
      const hasChildren = treeData[cacheKey];
      const isSelected = selectedPath === item.path;
      const isExpanded = expanded.includes(item.path);
      const hasLoadedChildren = hasChildren && hasChildren.length > 0;

      let children = null;
      if (hasLoadedChildren) {
        children = renderTree(hasChildren, repoPath);
      } else if (loading && isExpanded) {
        children = <TreeItem nodeId={`${item.path}-loading`} label="加载中..." />;
      }

      // 手动处理展开/收起
      const handleIconClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isExpanded) {
          setExpanded(expanded.filter(id => id !== item.path));
        } else {
          setExpanded([...expanded, item.path]);
          if (!hasChildren && repoPath) {
            loadTreeItems(item.path, repoPath);
          }
        }
      };

      // 自定义 label，包含箭头、Checkbox 和文件夹名
      const customLabel = (
        <Box display="flex" alignItems="center">
          {/* 展开/收起箭头 */}
          <Box
            onClick={handleIconClick}
            style={{
              cursor: 'pointer',
              marginRight: 4,
              width: 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
          </Box>

          {/* Checkbox */}
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => handlePathSelect(item.path)}
            onClick={(e) => e.stopPropagation()}
            style={{ marginRight: 8, cursor: 'pointer' }}
          />

          {/* 文件夹图标和名称 */}
          <FolderIcon style={{ marginRight: 4 }} fontSize="small" />
          <span>{item.name}</span>
        </Box>
      );

      return (
        <TreeItem
          key={item.path}
          nodeId={item.path}
          label={customLabel}
          className={classes.treeItem}
        >
          {children}
        </TreeItem>
      );
    });
  };

  return (
    <Box className={classes.root}>
      {/* 仓库信息 */}
      <TextField
        label={`${schema.title || 'GitLab 仓库'}${required ? ' *' : ''}`}
        value={projectPath}
        disabled
        fullWidth
        margin="dense"
        required={required}
        error={required && !selectedPath && rawErrors && rawErrors.length > 0}
      />

      {/* 帮助文本 */}
      {schema.description && (
        <FormHelperText>{schema.description}</FormHelperText>
      )}
      
      {/* 必填提示 */}
      {required && !selectedPath && (
        <FormHelperText error>
          此字段为必填项，请选择一个 K8s manifest 路径
        </FormHelperText>
      )}

      {/* 树形目录 */}
      {error ? (
        <FormHelperText error>{error}</FormHelperText>
      ) : (
        <Paper className={classes.paper} variant="outlined">
          <Typography variant="body2" color="textSecondary" gutterBottom>
            点击箭头展开/收起文件夹，勾选 checkbox 选择路径
          </Typography>
          {loading && expanded.length === 0 ? (
            <Box display="flex" alignItems="center">
              <CircularProgress size={20} />
              <Box ml={1}>加载目录结构中...</Box>
            </Box>
          ) : (
            <TreeView
              className={classes.treeView}
              expanded={expanded}
              onNodeToggle={handleToggle}
            >
              {projectPath && treeData[`${projectPath}:`] && renderTree(treeData[`${projectPath}:`], projectPath)}
            </TreeView>
          )}
        </Paper>
      )}

      {/* 已选择的路径 */}
      {selectedPath && (
        <Box className={classes.selectedPath}>
          <Typography variant="body2">
            <strong>✅ 已选择路径:</strong> {selectedPath}
          </Typography>
        </Box>
      )}

      {/* 错误提示 */}
      {rawErrors && rawErrors.length > 0 && (
        <FormHelperText error>{rawErrors.join(', ')}</FormHelperText>
      )}
    </Box>
  );
};

